/**
 * 純 JavaScript 的 SHA-256 實現，輸出十六進位小寫字串。
 *
 * 不使用 window.crypto.subtle 的原因：
 * 1. crypto.subtle 只在安全上下文（https / localhost）下可用，
 *    本機用 http://0.0.0.0:8080 或內網 IP 除錯時拿不到；
 * 2. 介面簽名需要在請求攔截器裡同步計算，自帶實現更省事也更好排查。
 *
 * 實現參照 FIPS 180-4，與 Java 端 MessageDigest("SHA-256") 的結果一致。
 */

/** 64 輪常量：前 64 個質數立方根小數部分的前 32 位 */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/** 初始雜湊值：前 8 個質數平方根小數部分的前 32 位 */
const H0 = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
  0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
];

/** 迴圈右移 */
function rotr(value, bits) {
  return (value >>> bits) | (value << (32 - bits));
}

/** 把字串轉為 UTF-8 位元組（不依賴 TextEncoder，相容舊 WebView） */
function toUtf8Bytes(message) {
  const bytes = [];
  for (let i = 0; i < message.length; i++) {
    const code = message.charCodeAt(i);
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // 代理對（emoji 等增補平面字元）需要合併成一個碼點
      const next = message.charCodeAt(++i);
      const point = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
      bytes.push(
        0xf0 | (point >> 18),
        0x80 | ((point >> 12) & 0x3f),
        0x80 | ((point >> 6) & 0x3f),
        0x80 | (point & 0x3f)
      );
    } else {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
  }
  return bytes;
}

/** 補位（0x80 + 0x00… + 64 位元長度）後按 64 位元組分塊壓縮，返回 8 個 32 位字 */
function digestWords(bytes) {
  const bitLength = bytes.length * 8;
  // 需要 1 位元組 0x80、8 位元組長度，並對齊到 64 位元組
  const totalLength = (((bytes.length + 9 + 63) >> 6) << 6);
  const buffer = new Uint8Array(totalLength);
  buffer.set(bytes);
  buffer[bytes.length] = 0x80;

  const view = new DataView(buffer.buffer);
  view.setUint32(totalLength - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(totalLength - 4, bitLength >>> 0);

  const w = new Int32Array(64);
  const hash = new Int32Array(H0);

  for (let offset = 0; offset < totalLength; offset += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = view.getInt32(offset + i * 4);
    }
    for (let i = 16; i < 64; i++) {
      const x = w[i - 15];
      const y = w[i - 2];
      const s0 = rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3);
      const s1 = rotr(y, 17) ^ rotr(y, 19) ^ (y >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }

    let a = hash[0];
    let b = hash[1];
    let c = hash[2];
    let d = hash[3];
    let e = hash[4];
    let f = hash[5];
    let g = hash[6];
    let h = hash[7];

    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + K[i] + w[i]) | 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) | 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) | 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) | 0;
    }

    hash[0] = (hash[0] + a) | 0;
    hash[1] = (hash[1] + b) | 0;
    hash[2] = (hash[2] + c) | 0;
    hash[3] = (hash[3] + d) | 0;
    hash[4] = (hash[4] + e) | 0;
    hash[5] = (hash[5] + f) | 0;
    hash[6] = (hash[6] + g) | 0;
    hash[7] = (hash[7] + h) | 0;
  }

  return hash;
}

/** 每個 32 位字固定輸出 8 位十六進位（不使用 padStart，相容舊環境） */
function toHex(value) {
  return ('00000000' + (value >>> 0).toString(16)).slice(-8);
}

/**
 * 計算 SHA-256
 * @param {String} message 待簽名的原文
 * @returns {String} 十六進位小寫摘要（64 個字元）
 */
export function sha256Hex(message) {
  const hash = digestWords(toUtf8Bytes(String(message)));
  let hex = '';
  for (let i = 0; i < hash.length; i++) {
    hex += toHex(hash[i]);
  }
  return hex;
}

export default sha256Hex;
